import { FormControl, FormLabel, Textarea } from '@chakra-ui/react';
import type { TextareaProps } from '@chakra-ui/react';
import { FormFieldMeta } from '@repo/shared/shared';
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { REQUIRED_FIELD_MSG } from '@/shared/constants';

type TextareaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = TextareaProps & {
  control?: Control<TFieldValues>;
  name: TName;
  validationParameters?: Omit<
    RegisterOptions<TFieldValues, TName>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >;
  label?: string;
  helperText?: string;
};

const TextareaField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  validationParameters = {},
  helperText,
  isRequired,
  label,
  onChange,
  onBlur,
  ...rest
}: TextareaFieldProps<TFieldValues, TName>) => {
  const {
    fieldState: { invalid, error },
    field: { onChange: onFieldChange, onBlur: onFieldBlur, ...restFiledProps },
  } = useController({
    name,
    control,
    rules: isRequired
      ? {
          required: REQUIRED_FIELD_MSG(label),
          ...validationParameters,
        }
      : validationParameters,
  });

  const handleChange: TextareaProps['onChange'] = (event) => {
    onFieldChange(event);
    onChange?.(event);
  };

  const handleBlur: TextareaProps['onBlur'] = (event) => {
    onFieldBlur();
    onBlur?.(event);
  };

  return (
    <FormControl
      isInvalid={invalid}
      isRequired={isRequired}
    >
      {label && (
        <FormLabel
          color='primary.pure'
          mb='1'
        >
          {label}
        </FormLabel>
      )}
      <Textarea
        onBlur={handleBlur}
        onChange={handleChange}
        {...rest}
        {...restFiledProps}
      />
      <FormFieldMeta
        errors={{ [name]: error }}
        helperText={helperText}
        name={name}
      />
    </FormControl>
  );
};

export default TextareaField;
